import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  GlobalStyles,
  InputAdornment,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";

import { viewportCappedHeightSx } from "config";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomScrollbars from "../CustomScrollbars";
import ColorizedLogText from "./ColorizedLogText";
import CountChip from "./CountChip";
import LogRecordList from "./LogRecordList";
import ManualResolveDialog from "./ManualResolveDialog";
import {
  useLazyExportLogRecordsQuery,
  useLazyGetJobFileLogsQuery,
  useLazyGetJobLogRecordsQuery,
  useLazyGetJobStatusLogsQuery,
  useLazyGetTaskFileLogsQuery,
  useLazyGetTaskLogRecordsQuery,
  useLazyGetTaskStatusLogsQuery,
} from "../../features/adminUsers/adminUsers";
import { botsManagementWs, BotsManagementWsEvent } from "../../features/ws/botsManagementWs";
import {
  countsForStream,
  fileCountsOf,
  LogRecordDto,
  LogRecordsResponse,
  logSelectionQuery,
  LogsScope,
  LogState,
  LogStream,
  ManualResolutionKind,
  manualResolutionIdentity,
} from "./logRecords";
import { LOG_RANGE_HINT } from "./rosterFile";

export type { LogsScope } from "./logRecords";

const PAGE_SIZE = 300;
const MAX_REFRESH_LIMIT = 2000;
const AUTO_REFRESH_MS = 30_000;

const TABS: Array<{ value: LogStream; label: string }> = [
  // All logs lists employees, each opening onto its own files; a Status logs
  // row opens onto the same files (CAL-6160).
  { value: "employees", label: "All logs" },
  { value: "file", label: "File logs" },
  { value: "status", label: "Status logs" },
];

const FILTERS: Array<{ value: LogState; label: string }> = [
  { value: "all", label: "All" },
  { value: "error", label: "Errors" },
  { value: "success", label: "Success" },
  { value: "warning", label: "Warnings" },
  { value: "skipped", label: "Skipped" },
  { value: "resolved", label: "Resolved" },
];

const LOG_SURFACE_SX = {
  bgcolor: "#ffffff",
  border: 1,
  borderColor: "divider",
  borderRadius: 1,
  display: "flex",
  overflow: "hidden",
};

// HighlightedText marks live under this class; the file-preview provider defines
// it too, and this viewer is mounted outside that tree.
const searchMarkStyles = (
  <GlobalStyles
    styles={{
      "mark.preview-search-mark": {
        backgroundColor: "#ffe066",
        color: "inherit",
        padding: "0 1px",
        borderRadius: 2,
      },
    }}
  />
);

/**
 * The logs of a job (or of one task) as the state they are in now.
 *
 * Three tabs over the two curated streams, each searchable, filterable by state
 * and counted over the whole scope rather than the page on screen. A failure a
 * later run fixed is shown as the success that fixed it, so "Errors 0" means
 * nothing is outstanding - which is the question an operator actually has. The
 * Raw switch falls back to the unprocessed text the bot wrote.
 */
export default function LogsView({
  scope,
  heightSx = viewportCappedHeightSx(200, 520, 400),
  canEdit = false,
  readOnlyReason = null,
  initialSearch,
}: {
  scope: LogsScope;
  /** Caps the scroll area; defaults to "as designed, but never past the viewport". */
  heightSx?: SxProps<Theme>;
  /** May the operator mark files as manually resolved (anywhere but Client View). */
  canEdit?: boolean;
  /** Why not, when canEdit is false; shown as the action's tooltip. */
  readOnlyReason?: string | null;
  /** Search prefilled when the view mounts - a deep link onto one employee. */
  initialSearch?: string;
}) {
  const [fetchJobRecords] = useLazyGetJobLogRecordsQuery();
  const [fetchTaskRecords] = useLazyGetTaskLogRecordsQuery();
  const [fetchJobStatusRaw] = useLazyGetJobStatusLogsQuery();
  const [fetchJobFileRaw] = useLazyGetJobFileLogsQuery();
  const [fetchTaskStatusRaw] = useLazyGetTaskStatusLogsQuery();
  const [fetchTaskFileRaw] = useLazyGetTaskFileLogsQuery();
  const [fetchExport] = useLazyExportLogRecordsQuery();

  const [stream, setStream] = useState<LogStream>("employees");
  const [stateFilter, setStateFilter] = useState<LogState>("all");
  // Seeded at mount on purpose: set later by an effect, the first (unfiltered)
  // load raced the filtered one and could land last.
  const [searchInput, setSearchInput] = useState(initialSearch ?? "");
  const [search, setSearch] = useState((initialSearch ?? "").trim());
  const [records, setRecords] = useState<LogRecordDto[]>([]);
  const [meta, setMeta] = useState<LogRecordsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState(false);
  const [rawText, setRawText] = useState<string>("");
  const [rawLoading, setRawLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [resolveTarget, setResolveTarget] = useState<LogRecordDto | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);

  const jobId = scope.kind === "job" ? scope.jobId : null;
  const taskId = scope.kind === "task" ? scope.taskId : null;
  const loadedRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The list pages through this selection and the CSV export holds all of it.
  const selection = useMemo(
    () => logSelectionQuery({ stream, state: stateFilter, search }),
    [search, stateFilter, stream],
  );

  const runQuery = useCallback(
    async (params: { offset: number; limit: number; refresh: boolean }) => {
      const common = { ...selection, ...params };
      return jobId != null
        ? fetchJobRecords({ jobId, ...common }, false).unwrap()
        : fetchTaskRecords({ taskId: taskId as number, ...common }, false).unwrap();
    },
    [fetchJobRecords, fetchTaskRecords, jobId, taskId, selection],
  );

  const exportCsv = async () => {
    setExporting(true);
    setExportError(null);
    try {
      const file = await fetchExport({ scope, ...selection }, false).unwrap();
      // A presigned S3 link that answers with Content-Disposition: attachment,
      // so the page stays where it is.
      const link = document.createElement("a");
      link.href = file.url;
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      setExportError("Failed to export the logs.");
    } finally {
      setExporting(false);
    }
  };

  const load = useCallback(
    async (mode: "reset" | "more" | "silent", refresh = false) => {
      if (mode === "more") setLoadingMore(true);
      if (mode === "reset") setLoading(true);
      try {
        const offset = mode === "more" ? loadedRef.current : 0;
        const limit =
          mode === "silent"
            ? Math.min(Math.max(loadedRef.current, PAGE_SIZE), MAX_REFRESH_LIMIT)
            : PAGE_SIZE;
        const answer = await runQuery({ offset, limit, refresh });
        setMeta(answer);
        setError(null);
        setRecords((prev) => {
          const next = mode === "more" ? [...prev, ...answer.records] : answer.records;
          loadedRef.current = next.length;
          return next;
        });
      } catch {
        if (mode !== "silent") {
          setError("Failed to load logs.");
          if (mode === "reset") {
            setRecords([]);
            loadedRef.current = 0;
          }
        }
      } finally {
        if (mode === "more") setLoadingMore(false);
        if (mode === "reset") setLoading(false);
      }
    },
    [runQuery],
  );

  // Filters, search and scope all restart the list from the first page: `load` is
  // rebuilt exactly when one of them changes.
  useEffect(() => {
    loadedRef.current = 0;
    void load("reset");
  }, [load]);

  // Quiet re-pull while the viewer is open: the counters and the visible window
  // stay current without a spinner or a scroll jump.
  useEffect(() => {
    const timer = setInterval(() => {
      void load("silent");
    }, AUTO_REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // WS fast path: refresh shortly after the bot reports something for this
  // scope instead of waiting out the 30s tick. The tick above stays as the
  // slow fallback (the records live in S3 - no event maps to them one to
  // one); events that do not name this scope are ignored.
  const wsReloadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const unsubscribe = botsManagementWs.subscribe((event: BotsManagementWsEvent) => {
      const matchesTask = taskId != null && event.task?.task_id === taskId;
      const matchesJob =
        jobId != null &&
        (event.job?.job_id === jobId || event.task?.job_id === jobId);
      if (!matchesTask && !matchesJob) return;
      if (wsReloadTimerRef.current) return;
      wsReloadTimerRef.current = setTimeout(() => {
        wsReloadTimerRef.current = null;
        void load("silent");
      }, 7000);
    });
    return () => {
      unsubscribe();
      if (wsReloadTimerRef.current) {
        clearTimeout(wsReloadTimerRef.current);
        wsReloadTimerRef.current = null;
      }
    };
  }, [jobId, taskId, load]);

  const loadRaw = useCallback(async () => {
    setRawLoading(true);
    try {
      // The raw download is of the streams themselves; the employees tab is a
      // view over both, so it hands back both files.
      const wanted: Array<"status" | "file"> =
        stream === "status" || stream === "file" ? [stream] : ["status", "file"];
      const chunks: string[] = [];
      for (const kind of wanted) {
        const answer =
          jobId != null
            ? kind === "file"
              ? await fetchJobFileRaw(jobId, false).unwrap()
              : await fetchJobStatusRaw(jobId, false).unwrap()
            : kind === "file"
              ? await fetchTaskFileRaw(taskId as number, false).unwrap()
              : await fetchTaskStatusRaw(taskId as number, false).unwrap();
        chunks.push(
          `--- ${kind === "file" ? "File logs" : "Status logs"} ---\n${answer.logs}`,
        );
      }
      setRawText(chunks.join("\n\n"));
    } catch {
      setRawText("(failed to load logs)");
    } finally {
      setRawLoading(false);
    }
  }, [
    fetchJobFileRaw,
    fetchJobStatusRaw,
    fetchTaskFileRaw,
    fetchTaskStatusRaw,
    jobId,
    taskId,
    stream,
  ]);

  useEffect(() => {
    if (!raw) return;
    void loadRaw();
  }, [raw, loadRaw]);

  const counts = useMemo(
    () => countsForStream(meta?.counts, stream),
    [meta?.counts, stream],
  );
  // The employees tab counts employees; its documents get a chip of their own
  // so the file failures stay visible beside them (CAL-6160).
  const fileCounts = useMemo(() => fileCountsOf(meta?.counts), [meta?.counts]);
  const range = meta?.range;
  const showTaskBadges = jobId != null;
  const outside = meta?.counts?.outside_ranges ?? 0;
  const unattributed = meta?.counts?.unattributed ?? 0;
  const notInRoster = meta?.counts?.not_in_roster ?? 0;
  const fromDeleted = meta?.counts?.from_deleted_tasks ?? 0;

  const refresh = () => {
    if (raw) {
      void loadRaw();
      return;
    }
    loadedRef.current = 0;
    void load("reset", true);
  };

  // "Manually resolved": offered on the employee rows of an upload or a download
  // job once the payload says so; the dialog needs the job even in task scope.
  const resolveJobId = jobId ?? meta?.job_id ?? null;
  const manualResolution = meta?.manual_resolution ?? null;
  const showResolveAction =
    resolveJobId != null &&
    manualResolution != null &&
    manualResolution.code !== "unsupported_job_kind" &&
    manualResolution.code !== "no_job";
  const resolveKind: ManualResolutionKind = manualResolution?.kind ?? "upload";
  const resolveDisabledReason = !canEdit
    ? readOnlyReason ?? "Read-only access."
    : manualResolution && !manualResolution.available
      ? manualResolution.reason
      : null;
  const openResolve = (record: LogRecordDto) => {
    setResolveTarget(record);
    setResolveOpen(true);
  };
  const handleResolved = () => {
    loadedRef.current = 0;
    void load("reset", true);
  };

  const rangeLabel = (() => {
    if (!range) return "";
    if (range.ranges_pending) return taskId != null ? "Range -" : "Employees -";
    if (range.from == null || range.to == null) return "";
    const window = `${range.from}-${range.to}`;
    if (taskId != null) return `Range ${window}`;
    return range.total_employees != null
      ? `Employees ${window} of ${range.total_employees}`
      : `Employees ${window}`;
  })();

  return (
    <Stack spacing={1} sx={{ minWidth: 0 }}>
      {searchMarkStyles}
      <Tabs
        value={stream}
        onChange={(_event: React.SyntheticEvent, value: LogStream) => setStream(value)}
        variant="scrollable"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: "divider", minHeight: 40 }}
      >
        {TABS.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            label={tab.label}
            sx={{ minHeight: 40, textTransform: "none" }}
          />
        ))}
      </Tabs>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        useFlexGap
        flexWrap={{ sm: "wrap" }}
        alignItems={{ xs: "stretch", sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search employee, file, reason..."
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          sx={{ minWidth: { xs: "auto", sm: 260 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <ClearIcon
                  fontSize="small"
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSearchInput("")}
                />
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
          {FILTERS.filter(
            (filter) =>
              (filter.value !== "warning" && filter.value !== "skipped") ||
              (filter.value === "warning" && counts.warning > 0) ||
              (filter.value === "skipped" && (counts.skipped ?? 0) > 0) ||
              stateFilter === filter.value,
          ).map((filter) => (
            <Chip
              key={filter.value}
              size="small"
              label={filter.label}
              onClick={() => setStateFilter(filter.value)}
              color={stateFilter === filter.value ? "primary" : "default"}
              variant={stateFilter === filter.value ? "filled" : "outlined"}
              sx={{ height: 24, fontSize: "0.72rem" }}
            />
          ))}
        </Stack>
        {/* Kept together: on a narrow dialog the group wraps to a line of its own. */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="flex-end"
          sx={{ ml: { sm: "auto" }, flexShrink: 0 }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={raw}
                onChange={(event) => setRaw(event.target.checked)}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                Raw
              </Typography>
            }
            sx={{ mr: 0 }}
          />
          <Button
            size="small"
            startIcon={
              loading || rawLoading ? <CircularProgress size={14} /> : <RefreshIcon />
            }
            disabled={loading || rawLoading}
            onClick={refresh}
          >
            Refresh
          </Button>
          {/* Raw has its own download: Download logs gives the raw files. */}
          {!raw ? (
            <Tooltip
              title={
                meta?.total_matched
                  ? stream === "employees"
                    ? `All ${meta.total_matched} employees this tab, filter and search match, each followed by its files, as a CSV file`
                    : `All ${meta.total_matched} records this tab, filter and search match, as a CSV file`
                  : "Nothing matches the filters."
              }
            >
              <span>
                <Button
                  size="small"
                  startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                  disabled={exporting || loading || !meta?.total_matched}
                  onClick={() => void exportCsv()}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  Export CSV
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      <Stack direction="row" sx={{ flexWrap: "wrap", gap: 0.5, minWidth: 0 }}>
        <CountChip label="Total" value={counts.total} />
        <CountChip label="Success" value={counts.success} color="success" />
        <CountChip
          label="Errors"
          value={counts.error}
          color="error"
          tooltip="Failures nothing has fixed yet"
        />
        {stream === "employees" && fileCounts.total > 0 ? (
          <CountChip
            label="Documents"
            value={fileCounts.total}
            tooltip="Every document of this scope, over all its employees. Open an employee to see its own."
          />
        ) : null}
        {stream === "employees" && fileCounts.error > 0 ? (
          <CountChip
            label="Documents failed"
            value={fileCounts.error}
            color="error"
            tooltip="Documents nothing has saved yet. The employee they belong to may still count as a success - open it to see which."
          />
        ) : null}
        {stream === "employees" && fileCounts.warning > 0 ? (
          <CountChip
            label="Document warnings"
            value={fileCounts.warning}
            color="warning"
            tooltip="Documents the bot could not take and does not count against the employee - the line says why."
          />
        ) : null}
        {stream === "employees" && (fileCounts.skipped ?? 0) > 0 ? (
          <CountChip
            label="Documents skipped"
            value={fileCounts.skipped ?? 0}
            tooltip="Documents the bot left alone on purpose - already in Data Storage, or outside what the job takes. The line says why."
          />
        ) : null}
        {counts.warning > 0 ? (
          <CountChip label="Warnings" value={counts.warning} color="warning" />
        ) : null}
        {(counts.skipped ?? 0) > 0 ? (
          <CountChip
            label="Skipped"
            value={counts.skipped ?? 0}
            tooltip="Documents the bot left alone on purpose - the line says why"
          />
        ) : null}
        {counts.resolved > 0 ? (
          <CountChip
            label="Fixed"
            value={counts.resolved}
            color="success"
            tooltip="Failed on an earlier run and completed since - not counted as errors"
          />
        ) : null}
        {counts.unknown > 0 ? (
          <CountChip
            label="Unrecognised"
            value={counts.unknown}
            tooltip="Records this viewer could not read; open one to see the raw text"
          />
        ) : null}
        {outside > 0 ? (
          <CountChip
            label="Outside ranges"
            value={outside}
            color="warning"
            tooltip="The employee is in the roster, but no task of this job covers their row - a coverage hole to fix"
          />
        ) : null}
        {notInRoster > 0 ? (
          <CountChip
            label="Not in the roster"
            value={notInRoster}
            tooltip="Records of employees the roster no longer holds - their work happened, it is just not this roster's work any more"
          />
        ) : null}
        {unattributed > 0 ? (
          <CountChip
            label="Unattributed"
            value={unattributed}
            tooltip="Records that could not be tied to a row of the roster file"
          />
        ) : null}
        {fromDeleted > 0 ? (
          <CountChip
            label="From deleted tasks"
            value={fromDeleted}
            tooltip="Records written by tasks this job no longer has - still counted as its work"
          />
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        {rangeLabel ? (
          <Tooltip title={LOG_RANGE_HINT}>
            <Typography
              variant="caption"
              sx={{ color: "text.secondary", alignSelf: "center", cursor: "help" }}
            >
              {rangeLabel}
            </Typography>
          </Tooltip>
        ) : null}
      </Stack>

      {range?.notice ? (
        <Typography variant="caption" sx={{ color: "warning.dark" }}>
          {range.notice}
        </Typography>
      ) : null}
      {error ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {error}
        </Typography>
      ) : null}
      {exportError ? (
        <Typography variant="caption" sx={{ color: "error.main" }}>
          {exportError}
        </Typography>
      ) : null}

      <Box sx={{ ...LOG_SURFACE_SX, ...heightSx }}>
        <CustomScrollbars>
          {raw ? (
            <Box
              component="pre"
              sx={{
                m: 0,
                p: 1.5,
                fontFamily: "monospace",
                fontSize: "0.78rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {rawLoading ? "(loading...)" : <ColorizedLogText text={rawText} />}
            </Box>
          ) : (
            <LogRecordList
              records={records}
              query={search}
              showTask={showTaskBadges}
              jobId={resolveJobId}
              taskId={taskId ?? null}
              showFiles={stream === "employees" || stream === "status"}
              loading={loading}
              emptyLabel={
                meta?.counts?.total
                  ? "No records match the filters."
                  : "No log records yet."
              }
              onResolve={showResolveAction ? openResolve : undefined}
              resolveKind={resolveKind}
              resolveDisabledReason={resolveDisabledReason}
            />
          )}
        </CustomScrollbars>
      </Box>

      {!raw ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {`Showing ${records.length} of ${meta?.total_matched ?? 0}`}
          </Typography>
          {meta?.has_more ? (
            <Button
              size="small"
              disabled={loadingMore}
              startIcon={loadingMore ? <CircularProgress size={14} /> : undefined}
              onClick={() => void load("more")}
            >
              Load more
            </Button>
          ) : null}
          <Box sx={{ flexGrow: 1 }} />
          {meta?.source ? (
            <Tooltip title={`Read at ${meta.generated_at}`}>
              <Typography variant="caption" sx={{ color: "text.disabled" }}>
                {meta.source === "s3_artifacts" ? "stored logs" : "local logs"}
              </Typography>
            </Tooltip>
          ) : null}
        </Stack>
      ) : null}

      <ManualResolveDialog
        key={resolveTarget ? manualResolutionIdentity(resolveTarget) : "none"}
        open={resolveOpen}
        jobId={resolveJobId}
        kind={resolveKind}
        record={resolveTarget}
        canEdit={canEdit}
        readOnlyReason={resolveDisabledReason}
        onClose={() => setResolveOpen(false)}
        onSaved={handleResolved}
      />
    </Stack>
  );
}
